import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { Badge } from '@/components/ui/badge';

const NotificationDropdown = ({ align = 'left' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
        { params: { user_id: user.id, limit: 20 } }
      );
      
      if (response.data.success) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        !(panelRef.current && panelRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId, isSystemMessage = false) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`,
        null,
        { params: isSystemMessage ? { user_id: user.id } : {} }
      );
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-all-read`,
        null,
        { params: { user_id: user.id } }
      );
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark notifications as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`
      );
      fetchNotifications();
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/clear-all`,
        { params: { user_id: user.id } }
      );
      fetchNotifications();
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read (pass system message flag)
    if (!notification.read) {
      await markAsRead(notification.id, notification.is_system_message);
    }

    // Special handling for job application notifications - go to hire-crew with job modal
    if (notification.type === 'job_application' && notification.job_id) {
      setIsOpen(false);
      navigate(`/hire-crew?openJob=${notification.job_id}`);
      return;
    }

    // Navigate if there's a link
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    } else if (notification.is_system_message) {
      // Just dismiss system message if no link
      setIsOpen(false);
    }
  };

  // Get icon and color based on notification type
  const getNotificationStyle = (type) => {
    switch (type) {
      case 'system_message':
        return { icon: '📢', color: 'blue' };
      case 'job_posted':
        return { icon: '💼', color: 'blue' };
      case 'message_received':
        return { icon: '💬', color: 'green' };
      case 'application_status':
        return { icon: '📋', color: 'purple' };
      case 'job_application':
        return { icon: '👤', color: 'orange' };
      case 'lead_match':
        return { icon: '🎯', color: 'pink' };
      default:
        return { icon: '🔔', color: 'gray' };
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        ref={btnRef}
        onClick={() => {
          if (!isOpen && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            // Clamp so the panel never runs off-screen (esp. inside the mobile drawer)
            const panelW = Math.min(384, window.innerWidth - 16);
            const left = Math.max(8, Math.min(r.left, window.innerWidth - panelW - 8));
            setMenuPos({ top: r.bottom + 8, left, right: Math.max(8, window.innerWidth - r.right) });
          }
          setIsOpen(!isOpen);
        }}
        className={`relative p-2 rounded-lg transition-colors ${
          isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'
        }`}
        data-testid="notifications-button"
      >
        <Bell className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: '#fff' }} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-[#28C0D0] rounded-full animate-pulse"></span>
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — portaled to body so it isn't clipped by any overflow:hidden ancestor */}
      {isOpen && menuPos && createPortal(
        <div
          ref={panelRef}
          className="w-96 max-w-[calc(100vw-2rem)]"
          style={{
            position: 'fixed',
            top: menuPos.top,
            ...(align === 'right' ? { right: menuPos.right } : { left: menuPos.left }),
            zIndex: 4000,
            background: 'rgba(10,14,26,.96)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,.1)',
            borderRadius: 16,
            boxShadow: '0 30px 70px -25px rgba(0,0,0,.85)',
            fontFamily: 'var(--sz-font-body)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(255,255,255,.08)' }}>
            <h3 className="text-lg" style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 700, color: '#fff' }}>
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-normal" style={{ color: 'rgba(255,255,255,.5)' }}>
                  ({unreadCount} new)
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button
                  onClick={clearAllNotifications}
                  className="text-xs px-2 py-1 rounded"
                  style={{ color: '#F87171' }}
                  title="Clear all notifications"
                >
                  Clear All
                </button>
              )}
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm"
                  style={{ color: '#28C0D0' }}
                  title="Mark all as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                style={{ color: 'rgba(255,255,255,.5)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-6 h-6 rounded-full mx-auto" style={{ border: '2px solid #28C0D0', borderTopColor: 'transparent' }}></div>
                <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,.5)' }}>Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 mx-auto mb-2" style={{ color: 'rgba(255,255,255,.25)' }} />
                <p className="font-medium" style={{ color: 'rgba(255,255,255,.6)' }}>No notifications yet</p>
                <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>
                  We'll notify you when something important happens
                </p>
              </div>
            ) : (
              notifications.map((notification) => {
                const style = getNotificationStyle(notification.type);
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="flex items-start gap-3 p-4 cursor-pointer transition-colors"
                    style={{
                      borderBottom: '1px solid rgba(255,255,255,.06)',
                      background: !notification.read ? 'rgba(40,192,208,.06)' : 'transparent',
                      borderLeft: notification.is_system_message ? '4px solid #60A5FA' : undefined,
                    }}
                  >
                    {/* Icon */}
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl"
                      style={{
                        background: notification.is_system_message
                          ? 'linear-gradient(135deg,#2563EB,#1D4ED8)'
                          : 'linear-gradient(135deg,#572B7C,#28C0D0)',
                      }}
                    >
                      {style.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-semibold text-sm" style={{ color: '#fff' }}>
                          {notification.is_system_message && (
                            <Badge className="mr-2 text-xs" style={{ background: '#60A5FA', color: '#0C1120' }}>SYSTEM</Badge>
                          )}
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: '#28C0D0' }}></div>
                        )}
                      </div>
                      <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,.55)' }}>
                        {notification.message}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>

                    {/* Delete button - hide for system messages */}
                    {!notification.is_system_message && (
                      <button
                        onClick={(e) => deleteNotification(notification.id, e)}
                        className="flex-shrink-0 p-1 rounded"
                        style={{ color: 'rgba(255,255,255,.4)' }}
                        title="Delete notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationDropdown;
